import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Banner,
  Button,
  Card,
  Chip,
  Field,
  Icon,
  Input,
  MoneyInput,
  RoleChip,
  Select,
  Textarea,
  Toggle,
  useToast,
} from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { isStaffCaller } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { ConsoleMfaControl } from '@/features/security/ConsoleMfaControl'
import { isConsoleMfaAgentLevel } from '@/features/security/consoleMfaCopy'
import {
  ROLE_LABEL,
  bpToCasinoSharePoints,
  bpToCommissionPercent,
  bpToSharePoints,
  casinoShareStep,
  canEditCasinoCurrency,
  commissionPercentToBp,
  editCreditCeiling,
  parentDownlineMaxMajor,
  displayToPaise,
  currencyLabel,
  formatMoney,
  isDirectChildOf,
  isMemberRole,
  levelToRole,
  maxCasinoShareOf,
  maxShareOf,
  minCasinoShareOf,
  minShareOf,
  formatSharePoints,
  myShareKeep,
  shareStep,
  nodeSharePoints,
  paiseToDisplay,
  parseCurrencyType,
  shareError,
  sharePayloadBp,
  parseCasinoCurrencyType,
  casinoCurrencyOptionsForSportsbook,
  resolveCasinoCurrencyForSportsbook,
  FUNDIST_CURRENCY_TYPES,
  parseFundistCurrencyType,
  lockedLoginUsername,
  BUSINESS_TYPES,
  B2C_CATEGORIES,
  B2C_CATEGORY_LABEL,
  b2cCategoryError,
  b2cCreateFields,
  businessTypeFromIsB2C,
  parseBusinessType,
  parseB2cCategory,
  type BusinessType,
  type B2cCategory,
  type FundistCurrencyType,
  type HierarchyNode,
} from './hierarchy'
import {
  confirmPasswordErrorOptional,
  isPasswordValid,
  passwordChecks,
  passwordErrorOptional,
} from './passwordPolicy'
import { PasswordInput } from './PasswordInput'
import './create.css'

const GAME_KEYS = [
  'casino',
  'cardGame',
  'liveGame',
  'sportsbook',
  'premiumSportsbook',
] as const

const GAME_LABEL: Record<(typeof GAME_KEYS)[number], string> = {
  casino: 'Casino',
  cardGame: 'Card game',
  liveGame: 'Live game',
  sportsbook: 'Sportsbook',
  premiumSportsbook: 'Premium sportsbook',
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
] as const

type FormState = {
  displayName: string
  comments: string
  creditLimit: string
  shareSportsbook: string
  shareCasino: string
  fancy: string
  bookmaker: string
  matchOdds: string
  minBet: string
  maxBet: string
  status: 'active' | 'inactive' | 'suspended' | 'closed'
  games: Record<(typeof GAME_KEYS)[number], boolean>
  /** Lab casinoCurrencyType — Evolution HKD|AED. */
  casinoCurrencyType: 'HKD' | 'AED'
  /** Lab fundistCurrencyType — SoftGamings Crash Games. */
  fundistCurrencyType: FundistCurrencyType
  /** CM→TM B2B / B2C channel (editable on TM). */
  businessType: BusinessType
  /** Required when businessType is B2C. */
  b2cCategory: B2cCategory | ''
  /** Optional new sign-in password — blank keeps current (Agent-UI parity). */
  password: string
  confirmPassword: string
}

function parseNumber(raw: string): number | null {
  const n = Number(String(raw).replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : null
}

function valuesFromNode(node: HierarchyNode): FormState {
  const status =
    node.status === 'inactive' || node.status === 'suspended' || node.status === 'closed'
      ? node.status
      : 'active'
  const share = nodeSharePoints(node)
  return {
    displayName: node.name,
    comments: node.comments ?? '',
    creditLimit: String(paiseToDisplay(node.creditLimit, parseCurrencyType(node.currencyType)) || ''),
    shareSportsbook: String(share.sportsbook),
    shareCasino: String(share.casino),
    fancy: String(bpToCommissionPercent(node.commissionBp ?? 0)),
    bookmaker: String(
      bpToCommissionPercent(node.commissionBookmakerBp ?? node.commissionBp ?? 0),
    ),
    matchOdds: String(
      bpToCommissionPercent(node.commissionMatchOddsBp ?? node.commissionBp ?? 0),
    ),
    minBet: String(paiseToDisplay(node.betMin, parseCurrencyType(node.currencyType)) || 100),
    maxBet: node.betMax
      ? String(paiseToDisplay(node.betMax, parseCurrencyType(node.currencyType)) || '')
      : '',
    status,
    games: {
      casino: node.games?.casino !== false,
      cardGame: node.games?.cardGame !== false,
      liveGame: node.games?.liveGame !== false,
      sportsbook: node.games?.sportsbook !== false,
      premiumSportsbook: !!node.games?.premiumSportsbook,
    },
    casinoCurrencyType: parseCasinoCurrencyType(node.casinoCurrencyType),
    fundistCurrencyType: parseFundistCurrencyType(node.fundistCurrencyType),
    businessType: businessTypeFromIsB2C(node.isB2C),
    b2cCategory: parseB2cCategory(node.b2cCategory) ?? '',
    password: '',
    confirmPassword: '',
  }
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="w7-create-section" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} className="w7-create-h2">
        {title}
      </h2>
      {children}
    </section>
  )
}

export function EditDownlinePage(props: { embedId?: string } = {}) {
  const { embedId } = props
  const { id: paramId = '' } = useParams<{ id: string }>()
  const id = embedId ?? paramId
  const embedded = Boolean(embedId)
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const ownerCm = Boolean(me.data) && !isStaffCaller(me.data?.caller)
  const [values, setValues] = useState<FormState | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const self = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
  })

  const agent = useQuery({
    queryKey: ['cm', 'hierarchy', 'agent', id],
    queryFn: () => cmApi.hierarchyAgent(authHeaders(), id),
    enabled: !!id,
  })

  useEffect(() => {
    if (agent.data?.node) setValues(valuesFromNode(agent.data.node))
  }, [agent.data?.node])

  const node = agent.data?.node
  const root = self.data?.node
  const isDirectChild = !!node && isDirectChildOf(node, root?.id)
  const role = node ? levelToRole(node.level) : null
  const isMember = isMemberRole(role)
  const currency = parseCurrencyType(node?.currencyType ?? root?.currencyType)
  // INR members: SportsBook forced to 100%. POINTS members: edit Master's keep.
  const showShareForm = !isMember || currency !== 'INR'

  const parentAvailable = paiseToDisplay(self.data?.availableCredit, currency)
  const childLimit = paiseToDisplay(node?.creditLimit, currency)
  const parentCap = parentDownlineMaxMajor(
    root?.downlineMaxCredit,
    node?.level ?? 0,
    currency,
  )
  const ceiling = editCreditCeiling(parentAvailable, childLimit, parentCap)

  const parentShareSb = bpToSharePoints(self.data?.node.shareBp ?? 0, currency)
  const parentShareCasino = bpToCasinoSharePoints(
    self.data?.node.shareCasinoBp ?? self.data?.node.shareBp ?? 0,
  )
  const maxShareSb = maxShareOf(parentShareSb, currency)
  const maxShareCasino = maxCasinoShareOf(currency)
  const minShareSb = minShareOf(parentShareSb, currency)
  const minShareCasino = minCasinoShareOf(currency)
  const shareInc = shareStep(currency)
  const casinoInc = casinoShareStep(currency)
  const parentFancy = bpToCommissionPercent(self.data?.node.commissionBp ?? 0)
  const parentBm = bpToCommissionPercent(
    self.data?.node.commissionBookmakerBp ?? self.data?.node.commissionBp ?? 0,
  )
  const parentMo = bpToCommissionPercent(
    self.data?.node.commissionMatchOddsBp ?? self.data?.node.commissionBp ?? 0,
  )

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setValues((prev) => (prev ? { ...prev, [key]: value } : prev))

  const credit = values ? parseNumber(values.creditLimit) : null
  const shareSb = values ? (parseNumber(values.shareSportsbook) ?? 0) : 0
  const shareCasino = values ? (parseNumber(values.shareCasino) ?? 0) : 0
  const fancy = values ? (parseNumber(values.fancy) ?? 0) : 0
  const bookmaker = values ? (parseNumber(values.bookmaker) ?? 0) : 0
  const matchOdds = values ? (parseNumber(values.matchOdds) ?? 0) : 0

  const errors = useMemo(() => {
    const e: Record<string, string | null> = {}
    if (!values) return e
    e.displayName = values.displayName.trim() ? null : 'Display name is required'
    e.b2cCategory = b2cCategoryError(values.businessType, values.b2cCategory)
    e.creditLimit =
      credit == null || credit < 1
        ? 'Credit limit must be at least 1'
        : credit > ceiling + 1e-9
          ? `Exceeds ceiling ${formatMoney(ceiling, currency)} (remaining + current limit${
              parentCap != null ? ' / parent max' : ''
            })`
          : null
    if (showShareForm) {
      e.shareSportsbook = shareError(shareSb, parentShareSb, 'sportsbook', currency)
      e.shareCasino = shareError(shareCasino, parentShareCasino, 'casino', currency)
    }
    e.fancy = fancy > parentFancy + 1e-9 ? `Max ${parentFancy.toFixed(2)}%` : null
    e.bookmaker = bookmaker > parentBm + 1e-9 ? `Max ${parentBm.toFixed(2)}%` : null
    e.matchOdds = matchOdds > parentMo + 1e-9 ? `Max ${parentMo.toFixed(2)}%` : null
    if (isMember) {
      const minB = parseNumber(values.minBet)
      const maxB = parseNumber(values.maxBet)
      e.minBet = minB == null || minB < 0 ? 'Enter a minimum bet' : null
      e.maxBet =
        maxB == null || maxB <= 0
          ? 'Enter a maximum bet'
          : minB != null && maxB < minB
            ? 'Max must be ≥ min'
            : null
    }
    e.password = passwordErrorOptional(values.password)
    e.confirmPassword = confirmPasswordErrorOptional(
      values.password,
      values.confirmPassword,
    )
    return e
  }, [
    values,
    credit,
    ceiling,
    parentCap,
    isMember,
    showShareForm,
    shareSb,
    shareCasino,
    parentShareSb,
    parentShareCasino,
    currency,
    fancy,
    bookmaker,
    matchOdds,
    parentFancy,
    parentBm,
    parentMo,
  ])

  const firstError = Object.entries(errors).find(([, v]) => v)?.[0] ?? null
  const ready = !firstError && isDirectChild

  const update = useMutation({
    mutationFn: async () => {
      setSubmitted(true)
      if (!values || !node) throw new Error('Account not loaded')
      if (!isDirectChild) throw new Error('You can only edit your direct downline')
      if (firstError) throw new Error(errors[firstError] || 'Fix the highlighted fields')
      const newPassword = values.password.trim()
      return cmApi.hierarchyUpdate(authHeaders(), {
        id: node.id,
        name: values.displayName.trim(),
        creditLimit: displayToPaise(credit!, currency),
        ...sharePayloadBp({
          isMember,
          sportsbookPoints: shareSb,
          casinoPoints: shareCasino,
          currency,
        }),
        ...(canEditCasinoCurrency({ actorLevel: root?.level, subjectRole: role })
          ? {
              casinoCurrencyType: resolveCasinoCurrencyForSportsbook(
                currency,
                values.casinoCurrencyType,
              ),
              fundistCurrencyType: parseFundistCurrencyType(values.fundistCurrencyType),
            }
          : {}),
        commissionBp: commissionPercentToBp(fancy),
        commissionBookmakerBp: commissionPercentToBp(bookmaker),
        commissionMatchOddsBp: commissionPercentToBp(matchOdds),
        status: values.status === 'closed' ? 'closed' : values.status,
        comments: values.comments.trim(),
        games: { ...values.games },
        ...(role === 'triple-master'
          ? b2cCreateFields(values.businessType, values.b2cCategory)
          : {}),
        ...(isMember
          ? {
              betMin: displayToPaise(parseNumber(values.minBet) ?? 0, currency),
              betMax: displayToPaise(parseNumber(values.maxBet) ?? 0, currency),
            }
          : {}),
        ...(newPassword ? { password: newPassword } : {}),
      })
    },
    onSuccess: (res) => {
      setValues((prev) =>
        prev ? { ...prev, password: '', confirmPassword: '' } : prev,
      )
      toast.success(`Updated ${res.node.name}`)
      void qc.invalidateQueries({ queryKey: ['cm', 'hierarchy'] })
      navigate('/hierarchy')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    update.mutate()
  }

  const showErr = (field: string) => (submitted ? errors[field] ?? null : null)

  if (agent.isLoading || self.isLoading || !values) {
    return (
      <div className="cm-page">
        <p className="cm-hint">Loading account…</p>
      </div>
    )
  }

  if (agent.isError) {
    return (
      <div className="cm-page">
        <Banner tone="error" title="Could not load account">
          {(agent.error as Error).message}
        </Banner>
        <Button type="button" variant="secondary" onClick={() => navigate('/hierarchy/list')}>
          Back to list
        </Button>
      </div>
    )
  }

  return (
    <div className="cm-page">
      {!embedded ? (
        <div className="w7-row" style={{ justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Edit downline</h1>
            <p className="cm-hint" style={{ marginTop: 6 }}>
              Username and level stay locked · credit, share, commission, status, games
            </p>
          </div>
          <div className="w7-row" style={{ gap: 8 }}>
            <Button type="button" variant="secondary" onClick={() => navigate('/hierarchy/list')}>
              Back to list
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/hierarchy')}>
              Tree view
            </Button>
          </div>
        </div>
      ) : null}

      {!isDirectChild ? (
        <Banner tone="warn" title="Edit is only for your direct downline">
          This account is further down the tree. Open a direct child to change credit, share, or
          status.
        </Banner>
      ) : (
        <Banner tone="info" title="Edit downline account">
          Ceiling {formatMoney(ceiling, currency)} (your remaining{' '}
          {formatMoney(parentAvailable, currency)} + current limit {formatMoney(childLimit, currency)}
          ).
        </Banner>
      )}

      <form className="w7-create" onSubmit={onSubmit} data-testid="edit-downline-form">
        <div className="w7-create-main">
          <Section id="identity" title="Identity">
            <Card title="Locked fields">
              <div className="w7-grid w7-grid-2">
                <Field label="Username">
                  {({ id: fid }) => (
                    <Input id={fid} value={lockedLoginUsername(node)} disabled readOnly />
                  )}
                </Field>
                <div>
                  <span className="w7-field-label">Role</span>
                  <div style={{ marginTop: 8 }}>
                    {role ? <RoleChip role={role} label={ROLE_LABEL[role]} /> : null}
                  </div>
                </div>
              </div>
            </Card>
            <Card title="Editable">
              <div className="w7-grid w7-grid-2">
                <Field label="Display name" required error={showErr('displayName')}>
                  {({ id: fid, describedBy, invalid }) => (
                    <Input
                      id={fid}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      value={values.displayName}
                      onChange={(e) => set('displayName', e.target.value)}
                      disabled={!isDirectChild}
                    />
                  )}
                </Field>
                <Field label="Status">
                  {({ id: fid, describedBy }) => (
                    <Select
                      id={fid}
                      aria-describedby={describedBy}
                      value={values.status === 'closed' ? 'closed' : values.status}
                      disabled={!isDirectChild}
                      onChange={(e) =>
                        set(
                          'status',
                          e.target.value as 'active' | 'inactive' | 'suspended' | 'closed',
                        )
                      }
                      options={[
                        ...STATUS_OPTIONS.map((opt) => ({
                          value: opt.value,
                          label: opt.label,
                        })),
                        ...(values.status === 'closed'
                          ? [{ value: 'closed' as const, label: 'Closed' }]
                          : []),
                      ]}
                    />
                  )}
                </Field>
                {role === 'triple-master' ? (
                  <>
                    <Field
                      label="Business type"
                      required
                      hint="B2B or B2C channel set at create — editable here."
                    >
                      {({ id: fid }) => (
                        <select
                          id={fid}
                          className="w7-input"
                          data-testid="edit-business-type"
                          value={values.businessType}
                          disabled={!isDirectChild}
                          onChange={(e) => {
                            const next = parseBusinessType(e.target.value)
                            setValues((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    businessType: next,
                                    b2cCategory: next === 'B2C' ? prev.b2cCategory : '',
                                  }
                                : prev,
                            )
                          }}
                        >
                          {BUSINESS_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                    {values.businessType === 'B2C' ? (
                      <Field
                        label="B2C category"
                        required
                        hint="Self deposit, Book system, or Both."
                        error={showErr('b2cCategory')}
                      >
                        {({ id: fid, describedBy, invalid }) => (
                          <select
                            id={fid}
                            className="w7-input"
                            data-testid="edit-b2c-category"
                            aria-describedby={describedBy}
                            aria-invalid={invalid || undefined}
                            value={values.b2cCategory}
                            disabled={!isDirectChild}
                            onChange={(e) =>
                              set('b2cCategory', parseB2cCategory(e.target.value) ?? '')
                            }
                          >
                            <option value="">Select…</option>
                            {B2C_CATEGORIES.map((c) => (
                              <option key={c} value={c}>
                                {B2C_CATEGORY_LABEL[c]}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                    ) : null}
                  </>
                ) : null}
              </div>
              <div className="w7-grid w7-grid-2" style={{ marginTop: 'var(--w7-s4)' }}>
                <Field
                  label="New password"
                  hint="Leave blank to keep the current password."
                  error={showErr('password')}
                >
                  {({ id: fid, describedBy, invalid }) => {
                    const passwordOk = isPasswordValid(values.password)
                    const passwordLiveInvalid = Boolean(values.password) && !passwordOk
                    return (
                      <>
                        <PasswordInput
                          id={fid}
                          describedBy={describedBy}
                          invalid={invalid || passwordLiveInvalid}
                          valid={Boolean(values.password) && passwordOk}
                          value={values.password}
                          onChange={(value) => set('password', value)}
                          autoComplete="new-password"
                          visible={showPassword}
                          onToggleVisible={() => setShowPassword((v) => !v)}
                          disabled={!isDirectChild}
                        />
                        {values.password ? (
                          <ul className="w7-create-pwchecks" aria-label="Password requirements">
                            {passwordChecks(values.password).map((check) => (
                              <li key={check.key} data-passed={check.passed ? 'true' : 'false'}>
                                <Icon name={check.passed ? 'check' : 'close'} size={12} />
                                {check.label}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </>
                    )
                  }}
                </Field>
                <Field label="Confirm password" error={showErr('confirmPassword')}>
                  {({ id: fid, describedBy, invalid }) => (
                    <PasswordInput
                      id={fid}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={values.confirmPassword}
                      onChange={(value) => set('confirmPassword', value)}
                      autoComplete="new-password"
                      visible={showConfirmPassword}
                      onToggleVisible={() => setShowConfirmPassword((v) => !v)}
                      disabled={!isDirectChild}
                    />
                  )}
                </Field>
              </div>
              <div style={{ marginTop: 'var(--w7-s4)' }}>
                <Field label="Comments">
                  {({ id: fid, describedBy }) => (
                    <Textarea
                      id={fid}
                      aria-describedby={describedBy}
                      value={values.comments}
                      onChange={(e) => set('comments', e.target.value)}
                      disabled={!isDirectChild}
                    />
                  )}
                </Field>
              </div>
            </Card>
          </Section>

          {ownerCm && node && isConsoleMfaAgentLevel(node.level) ? (
            <Section id="console-2fa" title="Console 2FA">
              <ConsoleMfaControl
                agentId={node.id}
                username={lockedLoginUsername(node)}
                level={node.level}
              />
            </Section>
          ) : null}

          <Section id="credit" title="Credit">
            <Card
              title="Credit limit"
              subtitle={`Raise or lower within your ceiling (${currencyLabel(currency)}).`}
            >
              <Field
                label="Credit limit"
                required
                hint={`Up to ${formatMoney(ceiling, currency)}.`}
                error={showErr('creditLimit')}
                aside={<Chip size="sm">Max {formatMoney(ceiling, currency)}</Chip>}
              >
                {({ id: fid, describedBy, invalid }) => (
                  <MoneyInput
                    id={fid}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    value={values.creditLimit}
                    onChange={(e) => set('creditLimit', e.target.value)}
                    disabled={!isDirectChild}
                  />
                )}
              </Field>
              {canEditCasinoCurrency({
                actorLevel: root?.level,
                subjectRole: role,
              }) ? (
                <Field
                  label="Casino Currency Flag"
                  required
                  hint={
                    currency === 'INR'
                      ? 'INR trees always use Evolution HKD (AED not allowed).'
                      : 'Lab Evolution currency (HKD or AED) for Points trees.'
                  }
                >
                  {({ id: fid }) => (
                    <select
                      id={fid}
                      className="w7-input"
                      value={resolveCasinoCurrencyForSportsbook(
                        currency,
                        values.casinoCurrencyType,
                      )}
                      onChange={(e) =>
                        set(
                          'casinoCurrencyType',
                          resolveCasinoCurrencyForSportsbook(
                            currency,
                            e.target.value,
                          ),
                        )
                      }
                      disabled={!isDirectChild || currency === 'INR'}
                    >
                      {casinoCurrencyOptionsForSportsbook(currency).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              ) : null}
              {canEditCasinoCurrency({
                actorLevel: root?.level,
                subjectRole: role,
              }) ? (
                <Field
                  label="Crash Games Currency"
                  required
                  hint="SoftGamings Fundist wallet currency (Lab fundistCurrencyType). Default HKD."
                >
                  {({ id: fid }) => (
                    <select
                      id={fid}
                      className="w7-input"
                      value={values.fundistCurrencyType}
                      onChange={(e) =>
                        set('fundistCurrencyType', parseFundistCurrencyType(e.target.value))
                      }
                      disabled={!isDirectChild}
                    >
                      {FUNDIST_CURRENCY_TYPES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              ) : null}
            </Card>
          </Section>

          <Section id="rates" title="Rates & commission">
            {showShareForm ? (
              <Card
                title="Partnership share"
                subtitle={
                  currency === 'INR'
                    ? 'SportsBook: Member Rate 0–1 (≥ yours). Casino: Member Rate 0–100.'
                    : 'POINTS SportsBook: My share any integer % (CM=0, player=100%). Casino: any numeric.'
                }
              >
                <div className="w7-grid w7-grid-2">
                  <Field
                    label={currency === 'INR' ? 'Member Rate' : 'My share'}
                    hint={
                      currency === 'INR'
                        ? `My Share: ${formatSharePoints(
                            myShareKeep(shareSb, parentShareSb, currency),
                            currency,
                          )} · (Min: ${formatSharePoints(minShareSb, currency)}) (Max: ${formatSharePoints(maxShareSb ?? 1, currency)})`
                        : 'Own keep % of pot (any integer ≥ 0)'
                    }
                    error={showErr('shareSportsbook')}
                  >
                    {({ id: fid, describedBy, invalid }) => (
                      <Input
                        id={fid}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        numeric
                        step={shareInc}
                        min={minShareSb}
                        {...(maxShareSb != null ? { max: maxShareSb } : {})}
                        value={values.shareSportsbook}
                        onChange={(e) => set('shareSportsbook', e.target.value)}
                        disabled={!isDirectChild}
                      />
                    )}
                  </Field>
                  <Field
                    label={currency === 'INR' ? 'Member Rate (Casino)' : 'My share (Casino)'}
                    hint={
                      currency === 'INR'
                        ? `(Min: 0) (Max: 100)`
                        : 'Point currency — any numeric value'
                    }
                    error={showErr('shareCasino')}
                  >
                    {({ id: fid, describedBy, invalid }) => (
                      <Input
                        id={fid}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        numeric
                        step={casinoInc}
                        min={minShareCasino}
                        max={maxShareCasino ?? undefined}
                        value={values.shareCasino}
                        onChange={(e) => set('shareCasino', e.target.value)}
                        disabled={!isDirectChild}
                      />
                    )}
                  </Field>
                </div>
              </Card>
            ) : (
              <Card
                title="Partnership share"
                subtitle="INR members: SportsBook 100%. Casino leaf 100."
              >
                <p className="w7-card-sub">
                  Sportsbook and casino share_bp stay at 10000 for INR players — nothing to edit.
                </p>
              </Card>
            )}

            <Card title="Commission">
              <div className="w7-grid w7-grid-3">
                <Field label="Fancy %" error={showErr('fancy')}>
                  {({ id: fid, describedBy, invalid }) => (
                    <Input
                      id={fid}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      numeric
                      suffix="%"
                      value={values.fancy}
                      onChange={(e) => set('fancy', e.target.value)}
                      disabled={!isDirectChild}
                    />
                  )}
                </Field>
                <Field label="Bookmaker %" error={showErr('bookmaker')}>
                  {({ id: fid, describedBy, invalid }) => (
                    <Input
                      id={fid}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      numeric
                      suffix="%"
                      value={values.bookmaker}
                      onChange={(e) => set('bookmaker', e.target.value)}
                      disabled={!isDirectChild}
                    />
                  )}
                </Field>
                <Field label="Match odds %" error={showErr('matchOdds')}>
                  {({ id: fid, describedBy, invalid }) => (
                    <Input
                      id={fid}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      numeric
                      suffix="%"
                      value={values.matchOdds}
                      onChange={(e) => set('matchOdds', e.target.value)}
                      disabled={!isDirectChild}
                    />
                  )}
                </Field>
              </div>
            </Card>
          </Section>

          <Section id="games" title={isMember ? 'Games & bet limits' : 'Games'}>
            <Card title="Game flags" flush>
              <ul className="w7-list">
                {GAME_KEYS.map((key) => (
                  <li key={key}>
                    <span className="w7-list-row">
                      <span>{GAME_LABEL[key]}</span>
                      <Toggle
                        checked={values.games[key]}
                        label={GAME_LABEL[key]}
                        disabled={!isDirectChild}
                        onChange={(checked) => set('games', { ...values.games, [key]: checked })}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
            {isMember ? (
              <Card title="Bet limits">
                <div className="w7-grid w7-grid-2">
                  <Field label="Minimum bet (₹)" required error={showErr('minBet')}>
                    {({ id: fid, describedBy, invalid }) => (
                      <MoneyInput
                        id={fid}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        value={values.minBet}
                        onChange={(e) => set('minBet', e.target.value)}
                        disabled={!isDirectChild}
                      />
                    )}
                  </Field>
                  <Field label="Maximum bet (₹)" required error={showErr('maxBet')}>
                    {({ id: fid, describedBy, invalid }) => (
                      <MoneyInput
                        id={fid}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        value={values.maxBet}
                        onChange={(e) => set('maxBet', e.target.value)}
                        disabled={!isDirectChild}
                      />
                    )}
                  </Field>
                </div>
              </Card>
            ) : null}
          </Section>
        </div>

        <aside className="w7-create-rail" id="review">
          <Card
            title="Save changes"
            className="w7-create-railcard"
            footer={
              <div className="w7-create-railfoot">
                <Button
                  type="submit"
                  variant="primary"
                  busy={update.isPending}
                  disabled={!isDirectChild}
                >
                  Save {role ? ROLE_LABEL[role] : 'account'}
                </Button>
                {!ready && submitted && firstError ? (
                  <p className="w7-create-block">{errors[firstError]}</p>
                ) : null}
              </div>
            }
          >
            <div className="w7-create-review" data-ready={ready ? 'true' : 'false'}>
              <div className="w7-create-review-status">
                <span className="w7-create-review-status-icon">
                  <Icon name={ready ? 'check' : 'warning'} size={18} />
                </span>
                <div>
                  <p className="w7-create-review-kicker">Ready check</p>
                  <p className="w7-create-review-status-title">
                    {ready ? 'Ready to save' : 'Complete the form'}
                  </p>
                </div>
              </div>
              <dl className="w7-create-review-facts">
                <div>
                  <dt>Name</dt>
                  <dd>{values.displayName.trim() || '—'}</dd>
                </div>
                <div>
                  <dt>Credit</dt>
                  <dd>{credit != null ? formatMoney(credit, currency) : '—'}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{values.status}</dd>
                </div>
                {values.password.trim() ? (
                  <div>
                    <dt>Password</dt>
                    <dd>Will update on save</dd>
                  </div>
                ) : null}
                {role === 'triple-master' ? (
                  <>
                    <div>
                      <dt>Business type</dt>
                      <dd>{values.businessType}</dd>
                    </div>
                    {values.businessType === 'B2C' ? (
                      <div>
                        <dt>B2C category</dt>
                        <dd>
                          {values.b2cCategory
                            ? B2C_CATEGORY_LABEL[values.b2cCategory]
                            : '—'}
                        </dd>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </dl>
            </div>
          </Card>
        </aside>

        <div className="w7-create-actionbar">
          <div className="w7-create-submit">
            <Button
              type="submit"
              variant="primary"
              busy={update.isPending}
              disabled={!isDirectChild}
            >
              Save {role ? ROLE_LABEL[role] : 'account'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
